// Snapshot → @rigid/viewer graph: nodes = tasks, projects, people, repos; edges = the relations in the edges table.
import type { ViewerEdge, ViewerGraph, ViewerNode } from "@rigid/viewer";
import type { LayoutEdge, LayoutNode, LayoutResultNode } from "@rigid/viewer/layout";
import type { EdgeKind, GraphData } from "@/api";
import { DEPENDENCY_COLOR, EDGE_COLOR, kindColor } from "./colors";

export const NODE_W = 200;
export const NODE_H = 44;

type Rel = { from: string; to: string; type: string; edgeKind: EdgeKind };

export type Shape = { nodes: (LayoutNode & { kind: string; name: string; status: string | null; color: string | null })[]; rels: Rel[] };

export function shapeOf(g: GraphData): Shape {
	const nodes: Shape["nodes"] = g.nodes.map((n) => ({ id: n.id, kind: n.kind, name: n.instance ? `${n.name} @${n.instance}` : n.name, status: n.status, color: n.color, width: NODE_W, height: NODE_H }));
	const ids = new Set(nodes.map((n) => n.id));
	// edge kinds are the relations' (the api reads them from the kinds)
	const rels: Rel[] = g.edges.filter((e) => ids.has(e.from) && ids.has(e.to)).map(({ from, to, type, edgeKind }) => ({ from, to, type, edgeKind }));
	return { nodes, rels };
}

/** Changes only when nodes or edges are added/removed — the layout is recomputed only then. */
export const topologyKey = (shape: Shape) =>
	`${shape.nodes.map((n) => n.id).join(",")}|${shape.rels.map((r) => `${r.from}>${r.to}:${r.type}`).join(",")}`;

export const layoutInput = (shape: Shape): { nodes: LayoutNode[]; edges: LayoutEdge[] } => ({
	nodes: shape.nodes.map(({ id, width, height, kind }) => ({ id, width, height, kind })),
	edges: shape.rels.map((r, i) => ({ id: `e${i}`, source: r.from, target: r.to, edgeKind: r.edgeKind })),
});

export function toViewerGraph(shape: Shape, positions: readonly LayoutResultNode[], dark: boolean): ViewerGraph {
	const at = new Map(positions.map((p) => [p.id, p]));
	const nodes: ViewerNode[] = shape.nodes.flatMap((n) => {
		const p = at.get(n.id);
		if (!p) return [];
		const color = n.color ?? kindColor(n.kind, dark);
		return [{ id: n.id, kind: n.kind, name: n.name, x: p.x, y: p.y, width: p.width, height: p.height, color, spec: { status: n.status } }];
	});
	const edges: ViewerEdge[] = shape.rels.map((r) => ({
		fromId: r.from,
		toId: r.to,
		type: r.type,
		edgeKind: r.edgeKind,
		color: (r.edgeKind === "dependency" ? DEPENDENCY_COLOR : EDGE_COLOR)[dark ? "dark" : "light"],
		...(r.edgeKind === "association" ? { dash: [4, 4] } : {}),
	}));
	return { nodes, edges };
}
